import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../store/hooks';
import { RootState } from '../store/store';
import { loadSurveyStatistics, loadSeriesStatistics, clearStatistics } from '../features/surveys/surveyStatisticsSlice';
import { loadSurveyById } from '../features/surveys/surveysSlice';
import { SurveyCharts } from '../components/SurveyCharts';
import { Icon } from '@iconify/react';
import type { QuestionStatistics } from '@workspace/types';
import {
  detectCategoryFromPath,
  getAssessmentListLabel,
  getAssessmentListPath,
  getAssessmentSingularLabel,
  getAssessmentStatisticsTitle,
} from '../features/surveys/assessmentLabels';

function getPeriodTypeLabel(periodType: string): string {
  if (periodType === 'mensual') return 'Mensual';
  if (periodType === 'semestral') return 'Semestral';
  return 'Anual';
}

function getScoreBadgeClass(score: number): string {
  if (score >= 4) return 'bg-green-100 text-green-800';
  if (score >= 3) return 'bg-blue-100 text-blue-800';
  return 'bg-amber-100 text-amber-800';
}

function QuestionStatisticsSection({ questions }: { questions: QuestionStatistics[] }) {
  if (!questions.length) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 text-sm text-muted-foreground">
        No hay preguntas con puntaje calculable en las respuestas actuales.
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden shadow-sm">
      <div className="px-6 py-4 border-b border-border bg-muted/30">
        <h3 className="font-bold">Resultado por pregunta</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="bg-muted/50 text-muted-foreground font-medium border-b border-border">
            <tr>
              <th className="px-6 py-3">Pregunta</th>
              <th className="px-6 py-3">Respuestas</th>
              <th className="px-6 py-3">Promedio</th>
              <th className="px-6 py-3">Distribución</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {questions.map((q) => (
              <tr key={q.fieldId} className="hover:bg-muted/30 transition-colors align-top">
                <td className="px-6 py-4 font-medium">{q.label}</td>
                <td className="px-6 py-4">{q.responseCount}</td>
                <td className="px-6 py-4">
                  {q.average !== null ? (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getScoreBadgeClass(q.average)}`}>
                      {q.average.toFixed(2)}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-6 py-4 text-muted-foreground">
                  {Object.entries(q.distribution)
                    .slice(0, 5)
                    .map(([value, count]) => `${value} (${count})`)
                    .join(' · ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function SurveyStatisticsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const category = detectCategoryFromPath(location.pathname);
  const listPath = getAssessmentListPath(category);
  const listLabel = getAssessmentListLabel(category);
  const singularLabel = getAssessmentSingularLabel(category);
  const { data: stats, seriesData, loading, seriesLoading, error } = useSelector((state: RootState) => state.surveyStatistics);
  const { currentSurvey } = useSelector((state: RootState) => state.surveys);
  const [years, setYears] = useState(5);

  useEffect(() => {
    if (id) {
      dispatch(loadSurveyById(id));
      dispatch(loadSurveyStatistics({ surveyId: id, years }));
    }
    return () => {
      dispatch(clearStatistics());
    };
  }, [dispatch, id, years]);

  useEffect(() => {
    if (currentSurvey?.seriesId) {
      dispatch(loadSeriesStatistics({ seriesId: currentSurvey.seriesId }));
    }
  }, [dispatch, currentSurvey?.seriesId]);

  if (loading && !stats) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        <p className="mt-4 text-muted-foreground">Cargando estadísticas...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive flex flex-col items-center">
        <Icon icon="mdi:alert-circle" className="w-12 h-12 mb-2" />
        <h3 className="text-lg font-bold">Error al cargar estadísticas</h3>
        <p className="mb-4">{error}</p>
        <button onClick={() => navigate(listPath)} className="btn btn-secondary">
          Volver a {listLabel}
        </button>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="space-y-6 pb-12">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
            <button onClick={() => navigate(listPath)} className="hover:text-foreground transition-colors">
              {listLabel}
            </button>
            <Icon icon="mdi:chevron-right" />
            <span>Estadísticas</span>
          </div>
          <h1 className="text-3xl font-bold">{currentSurvey?.title || getAssessmentStatisticsTitle(category)}</h1>
          <p className="text-muted-foreground">
            {stats.type === 'periodic'
              ? `Análisis evolutivo (${getPeriodTypeLabel(stats.periodType)})`
              : `Análisis acumulativo de ${singularLabel} permanente`}
          </p>
        </div>
        <div className="flex gap-2">
          {stats.type === 'periodic' && (
            <div className="flex items-center gap-2 bg-card border border-border rounded-md px-3 py-1">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Histórico:</label>
              <select 
                value={years} 
                onChange={(e) => setYears(parseInt(e.target.value))}
                className="bg-transparent text-sm font-bold focus:outline-none"
              >
                <option value={1}>1 año</option>
                <option value={2}>2 años</option>
                <option value={3}>3 años</option>
                <option value={5}>5 años</option>
              </select>
            </div>
          )}
          <button
            onClick={() => navigate(listPath)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border border-input bg-background hover:bg-muted transition-colors"
          >
            <Icon icon="mdi:arrow-left" className="w-4 h-4" />
            Volver
          </button>
        </div>
      </div>

      <section className="space-y-3">
        <h2 className="text-lg font-bold">Resultados generales</h2>
        <SurveyCharts data={stats} />
      </section>

      {stats.questionScores && stats.questionScores.length > 0 && (
        <QuestionStatisticsSection questions={stats.questionScores} />
      )}

      {currentSurvey?.seriesId && (
        <div className="bg-card border border-border rounded-lg overflow-hidden shadow-sm">
          <div className="px-6 py-4 border-b border-border bg-muted/30">
            <h3 className="font-bold">Línea de tiempo de serie ({currentSurvey.seriesId})</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Agrupado por período declarado de cada encuesta-unidad, no por fecha de respuesta.
            </p>
          </div>
          {seriesLoading && !seriesData && (
            <p className="px-6 py-4 text-sm text-muted-foreground">Cargando serie...</p>
          )}
          {seriesData && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-muted/50 text-muted-foreground font-medium border-b border-border">
                  <tr>
                    <th className="px-6 py-3">Período</th>
                    <th className="px-6 py-3">Encuesta</th>
                    <th className="px-6 py-3">Respuestas</th>
                    <th className="px-6 py-3">Puntaje total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {seriesData.units.map((unit) => (
                    <tr key={unit.assessmentId} className="hover:bg-muted/30 transition-colors">
                      <td className="px-6 py-4 font-bold">{unit.periodLabel ?? '—'}</td>
                      <td className="px-6 py-4">{unit.title}</td>
                      <td className="px-6 py-4">{unit.responseCount}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getScoreBadgeClass(unit.averageWeightedScore)}`}>
                          {unit.averageWeightedScore.toFixed(2)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {stats.type === 'periodic' && (
        <div className="bg-card border border-border rounded-lg overflow-hidden shadow-sm">
          <div className="px-6 py-4 border-b border-border bg-muted/30">
            <h3 className="font-bold">Tabla Comparativa por Período</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-muted/50 text-muted-foreground font-medium border-b border-border">
                <tr>
                  <th className="px-6 py-3">Período</th>
                  <th className="px-6 py-3">Respuestas</th>
                  <th className="px-6 py-3">Puntaje Total</th>
                  {stats.periods[0]?.groupScores.map(gs => (
                    <th key={gs.groupId} className="px-6 py-3">{gs.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {stats.periods.map((period) => (
                  <tr key={period.period} className="hover:bg-muted/30 transition-colors">
                    <td className="px-6 py-4 font-bold">{period.period}</td>
                    <td className="px-6 py-4">{period.responseCount}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getScoreBadgeClass(period.averageWeightedScore)}`}>
                        {period.averageWeightedScore.toFixed(2)}
                      </span>
                    </td>
                    {period.groupScores.map(gs => (
                      <td key={gs.groupId} className="px-6 py-4 text-muted-foreground">
                        {gs.weightedScore.toFixed(2)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
